import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { PageHero } from "@/components/site/PageHero";
import { InstantQuoteCalculator, type QuoteInitial } from "@/components/site/InstantQuoteCalculator";

const serviceIds = ["regular", "one-off", "deep", "tenancy", "military", "commercial", "airbnb"] as const;
const bedroomsIds = ["studio", "1", "2", "3", "4", "5+"] as const;
const bathroomsIds = ["1", "2", "3", "4+"] as const;
const receptionsIds = ["0", "1", "2", "3+"] as const;
const kitchenIds = ["small", "medium", "large"] as const;
const conditionIds = ["light", "standard", "heavy", "very-heavy"] as const;
const frequencyIds = ["one-off", "weekly", "fortnightly", "monthly", "tenancy", "march-out"] as const;
const propertyTypeIds = ["Flat", "Bungalow", "House", "Office", "Other"] as const;
const furnishedIds = ["furnished", "unfurnished", "partial"] as const;
const yesNo = ["yes", "no"] as const;
const addonIds = [
  "oven", "fridge", "cupboards", "extra-bathroom",
  "carpets", "after-builders",
] as const;

const searchSchema = z.object({
  service: z.enum(serviceIds).optional(),
  bedrooms: z.enum(bedroomsIds).optional(),
  bathrooms: z.enum(bathroomsIds).optional(),
  receptions: z.enum(receptionsIds).optional(),
  kitchen: z.enum(kitchenIds).optional(),
  condition: z.enum(conditionIds).optional(),
  frequency: z.enum(frequencyIds).optional(),
  propertyType: z.enum(propertyTypeIds).optional(),
  furnished: z.enum(furnishedIds).optional(),
  pets: z.enum(yesNo).optional(),
  parking: z.enum(yesNo).optional(),
  postcode: z.string().trim().max(12).optional(),
  date: z.string().trim().max(20).optional(),
  addons: z
    .string()
    .optional()
    .transform((s) =>
      s
        ? (s.split(",").map((x) => x.trim()).filter((x): x is (typeof addonIds)[number] =>
            (addonIds as readonly string[]).includes(x),
          ))
        : undefined,
    ),
  prefill: z.enum(["1", "true"]).optional(),
});

export const Route = createFileRoute("/quote")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Instant Cleaning Quote — IZHUB Cleaning Service" },
      { name: "description", content: "Get an instant cleaning quote in seconds. Choose your service, property details and add-ons — see your estimated price straight away." },
      { property: "og:title", content: "Instant Cleaning Quote — IZHUB Cleaning Service" },
      { property: "og:description", content: "See your estimated price instantly. WhatsApp, call or submit — reply usually within 2 hours." },
      { property: "og:url", content: "https://izhubcleaning.uk/quote" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/quote" }],
  }),
  component: Quote,
});

function Quote() {
  const search = Route.useSearch();
  const initial: QuoteInitial = {
    service: search.service,
    bedrooms: search.bedrooms,
    bathrooms: search.bathrooms,
    receptions: search.receptions,
    kitchen: search.kitchen,
    condition: search.condition,
    frequency: search.frequency,
    propertyType: search.propertyType,
    furnished: search.furnished,
    pets: search.pets,
    parking: search.parking,
    postcode: search.postcode?.toUpperCase(),
    date: search.date,
    addons: search.addons,
  };
  const prefilled =
    !!search.prefill ||
    Object.entries(initial).some(([k, v]) => k !== "service" && v !== undefined && (Array.isArray(v) ? v.length > 0 : true));

  return (
    <>
      <PageHero
        eyebrow="Instant quote"
        title={<>See your price <span className="rainbow-text">right now</span>.</>}
        intro="Pick your service, tell us about your property, and get an instant estimate — no waiting. Submit, WhatsApp or call when you're ready."
      />

      <section className="pb-24">
        <div className="container-x">
          <InstantQuoteCalculator initial={initial} prefilled={prefilled} />
        </div>
      </section>
    </>
  );
}
